import React, { useCallback, useEffect, useState } from 'react';
import { format, parseISO } from 'date-fns';
import Field from '../../../../components/Field';
import TextInput from '../../../../components/TextInput';
import PanelHeading from '../../../../components/PanelHeading';
import StatusPill from '../../../../components/StatusPill';
import { TableSkeleton } from '../../../../components/skeletons';
import {
  ChargeRate,
  ChargeRateFormData,
  FormErrors,
  formatCurrency,
  validateChargeRate,
  validateRequired
} from '../../config';

interface ChargeRatesTabProps {
  isOpen: boolean;
  employeeId: string;
  disabled: boolean;
  onAddChargeRate: (employeeId: string, formData: ChargeRateFormData) => Promise<boolean>;
  onFetchHistory: (employeeId: string) => Promise<ChargeRate[]>;
}

const today = () => format(new Date(), 'yyyy-MM-dd');

const emptyRate = (): ChargeRateFormData => ({
  charge_rate: '',
  effective_date: today(),
  end_date: ''
});

const formatDay = (date: string) => format(parseISO(date), 'd MMM yyyy');

/**
 * The charge rate in force and every rate before it. Adding a rate saves at
 * once, on its own button: it is a new row, not an edit to the employee, so it
 * is not part of the modal's Save.
 */
const ChargeRatesTab: React.FC<ChargeRatesTabProps> = ({
  isOpen,
  employeeId,
  disabled,
  onAddChargeRate,
  onFetchHistory
}) => {
  const [history, setHistory] = useState<ChargeRate[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [showForm, setShowForm] = useState<boolean>(false);
  const [form, setForm] = useState<ChargeRateFormData>(emptyRate);
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitting, setSubmitting] = useState<boolean>(false);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;

    setShowForm(false);
    setForm(emptyRate());
    setErrors({});
    setLoading(true);

    onFetchHistory(employeeId)
      .then((rows) => {
        if (!cancelled) setHistory(rows);
      })
      .catch((err) => {
        console.error('Failed to fetch charge rate history:', err);
        if (!cancelled) setHistory([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, employeeId, onFetchHistory]);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();

    const next: FormErrors = {};
    if (!validateRequired(form.charge_rate)) {
      next.charge_rate = 'Charge rate is required';
    } else if (!validateChargeRate(form.charge_rate)) {
      next.charge_rate = 'Enter a rate above zero';
    }
    if (!validateRequired(form.effective_date)) {
      next.effective_date = 'Effective date is required';
    }
    if (Object.keys(next).length > 0) {
      setErrors(next);
      return;
    }

    setSubmitting(true);
    try {
      const saved = await onAddChargeRate(employeeId, form);
      if (saved) {
        setShowForm(false);
        setForm(emptyRate());
        setErrors({});
        setHistory(await onFetchHistory(employeeId));
      }
    } catch (err) {
      console.error('Failed to add charge rate:', err);
      setErrors({ general: 'The rate could not be added. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  }, [employeeId, form, onAddChargeRate, onFetchHistory]);

  const current = history.find((rate) => rate.end_date === null);

  if (loading) {
    return (
      <div className="employee-tab" aria-busy="true">
        <TableSkeleton />
      </div>
    );
  }

  return (
    <div className="employee-tab">
      <PanelHeading title="Current rate" />
      <div className="stat-grid">
        <div className="stat">
          <span className="stat__value">
            {current ? formatCurrency(current.charge_rate) : '—'}
          </span>
          <span className="stat__label">
            {current ? `Per hour · from ${formatDay(current.effective_date)}` : 'No charge rate set'}
          </span>
        </div>
      </div>

      <PanelHeading
        title="Rate history"
        spaced
        meta={history.length > 0 ? history.length : undefined}
        action={!showForm && !disabled ? (
          <button
            type="button"
            className="panel-heading__action"
            onClick={() => setShowForm(true)}
          >
            + Add rate
          </button>
        ) : undefined}
      />

      {showForm && (
        <form className="disclosure-card employee-tab__disclosure" onSubmit={handleSubmit}>
          {errors.general && (
            <p className="field__error" role="alert">{errors.general}</p>
          )}
          <div className="pdm-grid pdm-grid--3">
            <Field label="Charge rate ($)" htmlFor="emp-rate" required error={errors.charge_rate}>
              <TextInput
                id="emp-rate"
                type="number"
                step="0.01"
                min="0"
                value={form.charge_rate}
                invalid={!!errors.charge_rate}
                disabled={submitting}
                onChange={(e) => setForm((prev) => ({ ...prev, charge_rate: e.target.value }))}
              />
            </Field>
            <Field label="Effective from" htmlFor="emp-rate-from" required error={errors.effective_date}>
              <TextInput
                id="emp-rate-from"
                type="date"
                value={form.effective_date}
                invalid={!!errors.effective_date}
                disabled={submitting}
                onChange={(e) => setForm((prev) => ({ ...prev, effective_date: e.target.value }))}
              />
            </Field>
            <Field
              label="End the current rate on"
              htmlFor="emp-rate-end"
              hint="Optional. Closes the rate in force now; the new rate is left open."
            >
              <TextInput
                id="emp-rate-end"
                type="date"
                value={form.end_date}
                disabled={submitting}
                onChange={(e) => setForm((prev) => ({ ...prev, end_date: e.target.value }))}
              />
            </Field>
          </div>
          <div className="disclosure-card__actions">
            <button
              type="button"
              className="pill-btn pill-btn--ghost pill-btn--sm"
              disabled={submitting}
              onClick={() => setShowForm(false)}
            >
              Cancel
            </button>
            <button type="submit" className="pill-btn pill-btn--primary pill-btn--sm" disabled={submitting}>
              {submitting ? 'Adding…' : 'Add rate'}
            </button>
          </div>
        </form>
      )}

      {history.length === 0 ? (
        <p className="ui-table__empty">No charge rates have been set for this person.</p>
      ) : (
        <table className="ui-table">
          <thead>
            <tr>
              <th className="is-num">Rate</th>
              <th>From</th>
              <th>To</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {history.map((rate) => (
              <tr key={rate.id}>
                <td className="is-num">{formatCurrency(rate.charge_rate)}</td>
                <td>{formatDay(rate.effective_date)}</td>
                <td>
                  {rate.end_date ? formatDay(rate.end_date) : <span className="ui-table__na">—</span>}
                </td>
                <td className="ui-table__actions">
                  {rate.end_date === null
                    ? <StatusPill label="Current" tone="success" />
                    : <StatusPill label="Ended" />}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default ChargeRatesTab;
